import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { InfoCircleIcon } from '@langchain/untitled-ui-icons';
import {
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
} from '@mui/joy';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@radix-ui/react-accordion';

import { ReactNode, useState } from 'react';
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { InformationalBanner } from '@/components/InformationalBanner';
import { Pane } from '@/components/Pane';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/Table';
import useToast from '@/components/Toast/useToast';
import {
  useOrganizationCreateRole,
  useOrganizationPatchRole,
} from '@/hooks/useSwr';
import {
  OrganizationCreateRoleRequest,
  OrganizationPatchRoleRequest,
  OrganizationRoleSchema,
  PermissionSchema,
  ResourceType,
} from '@/types/schema';

import { ReactHookFormRoleRequest } from '../types/organizationMembers.type';
import { ICON_MAP } from '../utils/ResourceIconMap';
import {
  isSingleOn,
  permToggleState,
  plural,
  toggleAllOff,
  toggleAllOn,
  toggleSingleOff,
} from '../utils/organizationMembers.utils';

type EditRolePermissionsProps = {
  permissions: FieldArrayWithId<
    ReactHookFormRoleRequest,
    'permissions',
    'id'
  >[];
  permissionGroups: Record<string, PermissionSchema[]>;
  append: UseFieldArrayAppend<ReactHookFormRoleRequest, 'permissions'>;
  remove: UseFieldArrayRemove;
  register: UseFormRegister<ReactHookFormRoleRequest>;
};

const resToDisplay = {
  'annotation-queues': {
    name: 'Annotation Queues',
    icon: ICON_MAP('size-4')[ResourceType.Queue],
  },
  charts: {
    name: 'Dashboards',
    icon: ICON_MAP('size-4')[ResourceType.Dashboard],
  },
  datasets: {
    name: 'Datasets',
    icon: ICON_MAP('size-4')[ResourceType.Dataset],
  },
  deployments: {
    name: 'Deployments',
    icon: ICON_MAP('size-4')[ResourceType.Deployment],
  },
  feedback: {
    name: 'Feedback',
    icon: ICON_MAP('size-4')['feedback'],
  },
  projects: {
    name: 'Tracing projects',
    icon: ICON_MAP('size-4')[ResourceType.Project],
  },
  prompts: {
    name: 'Prompts',
    icon: ICON_MAP('size-4')[ResourceType.Prompt],
  },
  runs: {
    name: 'Runs',
    icon: ICON_MAP('size-4')['project'],
  },
  workspaces: {
    name: 'Workspaces',
    icon: ICON_MAP('size-4')['workspace'],
  },
  rules: {
    name: 'Automations',
    icon: ICON_MAP('size-4')['rule'],
  },
};

function EditRolePermissions({
  permissions,
  permissionGroups,
  append,
  remove,
}: EditRolePermissionsProps) {
  return (
    <div>
      <FormLabel>Permissions</FormLabel>
      <Accordion type="multiple">
        {Object.entries(permissionGroups).map(([permRes, schemaPerms]) => {
          const { name, icon } = resToDisplay[permRes];
          const existingGroupPerms = permissions.filter((p) =>
            p.perm.startsWith(permRes)
          );
          const allPermState = permToggleState(schemaPerms, existingGroupPerms);
          return (
            <AccordionItem
              value={name}
              key={name}
              className="border-b border-secondary"
            >
              <AccordionTrigger className="flex w-full items-center gap-2 py-4 hover:bg-secondary-hover">
                {icon}
                <div className="flex-1 text-left capitalize">{name}</div>
                <div className="flex items-center gap-2 text-tertiary">
                  <span className="whitespace-nowrap">
                    {plural(existingGroupPerms.length, 'permission', 's')}
                  </span>
                  <ChevronDownIcon height={16}></ChevronDownIcon>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <Table className="table-fixed rounded-lg border border-b-0 border-secondary">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px] pl-5 leading-none">
                        <Checkbox
                          onChange={(e) => {
                            if (e.currentTarget.checked) {
                              toggleAllOn(
                                schemaPerms,
                                existingGroupPerms,
                                append
                              );
                            } else {
                              toggleAllOff(schemaPerms, permissions, remove);
                            }
                          }}
                          checked={allPermState.checked}
                          indeterminate={allPermState.indeterminate}
                        ></Checkbox>
                      </TableHead>
                      <TableHead>Permission</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schemaPerms.map((p) => {
                      const permName = p.name.split(':').at(1);
                      const checked = isSingleOn(p, existingGroupPerms);
                      const isWorkspaceRead = p.name === 'workspaces:read';
                      return (
                        <TableRow key={p.name}>
                          <TableCell className="w-[50px] pl-5 leading-none">
                            <Checkbox
                              checked={checked}
                              disabled={isWorkspaceRead}
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  append({ perm: p.name });
                                } else {
                                  toggleSingleOff(p, permissions, remove);
                                }
                              }}
                            ></Checkbox>
                          </TableCell>
                          <TableCell>
                            <span
                              className="flex items-center gap-2 capitalize"
                              style={{ minWidth: 70 }}
                            >
                              {permName}{' '}
                              {isWorkspaceRead && (
                                <Popover>
                                  <PopoverContent>
                                    Workspace read permission is assigned to
                                    every role by default.
                                  </PopoverContent>
                                  <PopoverTrigger>
                                    <InfoCircleIcon className="size-4 hover:cursor-pointer" />
                                  </PopoverTrigger>
                                </Popover>
                              )}
                            </span>
                            <span className="text-tertiary">
                              {p.description}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

type EditRoleFormProps = {
  permissionGroups: Record<string, PermissionSchema[]>;
  role?: OrganizationRoleSchema;
  onSubmit: (
    data: OrganizationCreateRoleRequest | OrganizationPatchRoleRequest
  ) => void;
};

function EditRoleForm({ role, onSubmit, permissionGroups }: EditRoleFormProps) {
  'use no memo';
  const { register, control, handleSubmit, formState } =
    useForm<ReactHookFormRoleRequest>({
      defaultValues: role
        ? {
            display_name: role.display_name,
            description: role.description,
            permissions: role.permissions.map((p) => ({ perm: p })),
          }
        : {
            permissions: [{ perm: 'workspaces:read' }],
          },
    });

  const {
    fields: permissions,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'permissions',
  });

  const wrappedSubmit = (editedRole: ReactHookFormRoleRequest) => {
    onSubmit({
      ...editedRole,
      permissions: editedRole.permissions.map((p) => p.perm),
    });
  };

  return (
    <form
      className="flex flex-col items-stretch gap-8"
      onSubmit={(e) => {
        e.stopPropagation();
        handleSubmit(wrappedSubmit)(e);
      }}
    >
      <FormControl error={!!formState.errors.display_name?.message}>
        <FormLabel>Role Name</FormLabel>
        <Input
          placeholder="Name..."
          {...register('display_name', {
            required: 'Name is required',
          })}
        />
        {formState.errors.display_name?.message && (
          <FormHelperText>
            {formState.errors.display_name?.message}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl error={!!formState.errors.description?.message}>
        <FormLabel>Description</FormLabel>
        <Input placeholder="Description..." {...register('description')} />
        {formState.errors.description?.message && (
          <FormHelperText>
            {formState.errors.description?.message}
          </FormHelperText>
        )}
      </FormControl>

      <EditRolePermissions
        append={append}
        remove={remove}
        permissions={permissions}
        register={register}
        permissionGroups={permissionGroups}
      ></EditRolePermissions>
      <div className="sticky bottom-0 right-4 bg-background py-4 text-right text-tertiary">
        <Button type="submit" loading={formState.isSubmitting}>
          {role ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

type EditRoleModalProps = {
  role?: OrganizationRoleSchema;
  permissionGroups: Record<string, PermissionSchema[]>;
  trigger: (onClick) => ReactNode;
};
export function EditRoleDrawer({
  role,
  permissionGroups,
  trigger,
}: EditRoleModalProps) {
  const [isOpen, setOpen] = useState(false);
  const createRole = useOrganizationCreateRole();
  const patchRole = useOrganizationPatchRole(role?.id);

  const { createToast } = useToast();
  const action = role ? patchRole : createRole;

  const doClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Pane
        open={isOpen}
        noBackArrow={true}
        onClose={doClose}
        className="p-0"
        preventInteractOutside={true}
        dialogStyle={{
          marginLeft: 'calc(max(4.5rem, 100vw - max(20vw, 550px)))',
        }}
        overlayClassName="z-[9999]"
        title={
          <div className="flex flex-col gap-0.5">
            <h3 className="text-xl font-medium">
              {role ? 'Edit' : 'Create'} role
            </h3>
            <p className="text-sm font-normal text-tertiary">
              {role ? 'Edit' : 'Create'} roles with customizable permissions
            </p>
          </div>
        }
        topBarRightElement={
          <button
            type="button"
            className="rounded-md p-1 pr-6 hover:bg-secondary-hover active:bg-secondary"
            onClick={doClose}
          >
            <XMarkIcon className="h-6 w-6 text-ls-black" />
          </button>
        }
      >
        <div className="px-6 pt-8">
          <InformationalBanner>
            Changes to roles and permissions may take a couple minutes to
            propagate.
          </InformationalBanner>
          <div className="mx-auto flex w-full max-w-[518px] flex-col gap-8">
            {action.error && <ExpandableErrorAlert error={action.error} />}

            <EditRoleForm
              permissionGroups={permissionGroups}
              role={role}
              onSubmit={async (mRole) => {
                await action.trigger({
                  json: mRole,
                });
                setOpen(false);
                createToast({
                  title: `${mRole.display_name} has been ${
                    role ? 'updated' : 'created'
                  }`,
                });
              }}
            />
          </div>
        </div>
      </Pane>

      {trigger(() => setOpen(true))}
    </>
  );
}
